import React, { useMemo } from 'react';
import { Layout, ContinuousLinearTracker, Badge } from '@americanexpress/dls-react';
import { useIntl } from 'react-intl';
import { IconCheck } from '@americanexpress/dls-icons';
import styles from '@americanexpress/web-react-extensions/styles/viewAll.scss';
import { MembershipTextBadge } from '@americanexpress/web-react-extensions';
import IconComponent from '../../shared/components/IconComponent';

const UnifiedTracker = (props) => {
  const intl = useIntl();

  // Destructure trackers
  const [orderOneTracker = {}, orderTwoTracker = {}] = props?.trackers || [];
  const isCurrency = props?.isCurrency || false;

  // Memoized values for Order One Tracker
  const orderOneTrackerData = useMemo(() => {
    const isAchieved = orderOneTracker?.status === 'ACHIEVED';
    const isInProgress = orderOneTracker?.status === 'IN_PROGRESS';
    const progressIcon = orderOneTracker?.metaContent?.content?.detail?.progress?.dlsicon || 'IconRewards';
    const achievedIcon = orderOneTracker?.metaContent?.content?.detail?.achieved?.dlsicon || 'IconRewards';
    const progressStatusMessage = orderOneTracker?.metaContent?.content?.detail?.progress?.statusMsg;
    const achievedStatusMessage = orderOneTracker?.metaContent?.content?.detail?.achieved?.statusMsg;
    const sorBenefitId = orderOneTracker.sorBenefitId;
    const benefitName = orderOneTracker.benefitName;

    return {
      isAchieved,
      isInProgress,
      progressIcon,
      achievedIcon,
      progressStatusMessage,
      achievedStatusMessage,
      sorBenefitId,
      benefitName,
    };
  }, [orderOneTracker]);

  // Memoized values for Order Two Tracker
  const orderTwoTrackerData = useMemo(() => {
    const tracker = orderTwoTracker?.tracker || {};
    const progress = orderTwoTracker?.progress;
    const updateInterval = progress?.updateInterval;
    const isAchieved = orderTwoTracker?.status === 'ACHIEVED';
    const isInProgress = orderTwoTracker?.status === 'IN_PROGRESS';
    const progressIcon = orderTwoTracker?.metaContent?.content?.detail?.progress?.dlsicon || 'IconDollar';
    const achievedIcon = orderTwoTracker?.metaContent?.content?.detail?.achieved?.dlsicon || 'IconRewards';
    const sorBenefitId = orderTwoTracker.sorBenefitId;
    const benefitName = orderTwoTracker.benefitName;
    const achievedStatusMessage = orderTwoTracker?.metaContent?.content?.detail?.achieved?.statusMsg;
    const progressStatusMessage = orderTwoTracker?.metaContent?.content?.detail?.progress?.statusMsg;

    return {
      tracker,
      progress,
      updateInterval,
      isAchieved,
      isInProgress,
      progressIcon,
      achievedIcon,
      sorBenefitId,
      benefitName,
      achievedStatusMessage,
      progressStatusMessage,
    };
  }, [orderTwoTracker]);

  // utility function
  const formattedAmount = (amount) => {
    let amnt = amount <= 0 ? 0 : amount;
    const convertToInt = Math.round(amnt).toLocaleString();

    const numberOptions = {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    };

    if (isCurrency) {
      return `${orderTwoTracker?.tracker?.targetCurrencySymbol}${convertToInt}`;
    }
    return intl?.formatNumber(amnt, numberOptions);
  };

  const renderTrackers = () => (
    <Layout
      id={`container-${orderOneTrackerData?.sorBenefitId}`}
      data-testid={`container-${orderOneTrackerData?.sorBenefitId}`}
      variant="card"
      className="border radius-container-sm margin-t"
      innerContainerClassName="pad-2 pad-3-lg-up flex-item-grow"
    >
      <div id={`progress-section-${orderTwoTrackerData?.benefitName}`} data-testid={`progress-section-${orderTwoTrackerData.benefitName}`} className="flex flex-column">
        {orderOneTrackerData?.progressStatusMessage && orderOneTrackerData?.isInProgress && (
          <MembershipTextBadge
            className="pad-1-tb"
            icon={(
              <IconComponent
                icon={orderOneTrackerData?.progressIcon}
                iconSize="sm"
                iconColor="white"
                isFilled={true}
              />
            )}
            status="informationAlt"
            size="small"
            type="text"
          >
            <span className="body-xsmall-bold pad-1-tb" dangerouslySetInnerHTML={{ __html: orderOneTrackerData?.progressStatusMessage }} />
          </MembershipTextBadge>
        )}
        {orderOneTrackerData?.achievedStatusMessage && orderOneTrackerData?.isAchieved && !orderTwoTrackerData?.isAchieved && (
          <MembershipTextBadge
            className="pad-1-tb"
            icon={(
              <IconComponent
                icon={orderOneTrackerData?.achievedIcon}
                iconSize="sm"
                iconColor="success"
                isFilled={true}
              />
            )}
            status="success"
            size="small"
            type="text"
          >
            <span className="body-xsmall-bold pad-1-tb" dangerouslySetInnerHTML={{ __html: orderOneTrackerData?.achievedStatusMessage }} />
          </MembershipTextBadge>
        )}
        {orderTwoTrackerData?.isInProgress && (
          <>
            <div className="flex flex-row">
              <div className="flex flex-column flex-item-grow margin-2-t stack-1">
                <ContinuousLinearTracker
                  id={`linear-tracker-${orderTwoTrackerData?.benefitName}`}
                  max={Number(orderTwoTrackerData?.tracker?.targetAmount)}
                  min={0}
                  value={Number(orderTwoTrackerData?.tracker?.spentAmount)}
                  color="brand"
                  hideTrackerEndText={true}
                  hideTrackerStartText={true}
                  data-testid={`linear-tracker-${orderTwoTrackerData?.benefitName}`}
                />
                <div id={`tracker-labels-${orderTwoTrackerData?.benefitName}`} data-testid={`tracker-labels-${orderTwoTrackerData?.benefitName}`}>
                  <div className="body-small-bold pull-left-xs-up">
                    <span className="color-text-brand">{`${formattedAmount(orderTwoTrackerData?.tracker?.spentAmount)}`} </span>
                    <span>{`${orderTwoTrackerData?.progress?.usedLabel}`}</span>
                  </div>
                  <div className="body-small-bold pull-right-xs-up">
                    <span>{`${formattedAmount(orderTwoTrackerData?.tracker?.remainingAmount)} ${orderTwoTrackerData?.progress?.togoLabel}`}</span>
                  </div>
                </div>
              </div>
              <IconComponent className="margin-1-l" icon={orderTwoTrackerData?.progressIcon} iconSize="md" iconColor="brand" isFilled={true} />
            </div>
            <div id={`description-${orderTwoTrackerData?.benefitName}`} data-testid={`description-${orderTwoTrackerData?.benefitName}`} className="margin-2-t margin-responsive-b">
              <span className="body" dangerouslySetInnerHTML={{ __html: orderTwoTrackerData?.progressStatusMessage }} />
            </div>
          </>
        )}
        {orderOneTrackerData?.isInProgress && orderTwoTrackerData?.isAchieved && (
          <Badge className="pad-1-tb" icon={<IconComponent icon={orderTwoTrackerData?.achievedIcon} iconSize="sm" iconColor="success" isFilled={true} />} status="success" size="small" type="text"><span className="body-xsmall-bold pad-1-tb" dangerouslySetInnerHTML={{ __html: orderTwoTrackerData?.achievedStatusMessage }} />
          </Badge>
        )}
      </div>
      {orderOneTrackerData?.isAchieved && orderTwoTrackerData?.isAchieved && (
        <div className="flex flex-row flex-align-center">
          <div className="flex pad-0-l flex-column flex-align-start col-xs-10">
            {orderOneTrackerData.achievedStatusMessage && (
              <Badge className="pad-1-tb" icon={<IconComponent icon={orderOneTrackerData?.achievedIcon} iconSize="sm" iconColor="success" isFilled={false} />} status="success" size="small" type="text"><span className="body-xsmall-bold pad-1-tb" dangerouslySetInnerHTML={{ __html: orderOneTrackerData?.achievedStatusMessage }} />
              </Badge>
            )}
            {orderTwoTrackerData?.achievedStatusMessage && (
              <Badge className="pad-3-tb" icon={<IconComponent icon={orderTwoTrackerData?.achievedIcon} iconSize="sm" iconColor="success" isFilled={false} />} status="success" size="small" type="text"><span className="body-xsmall-bold pad-1-tb" dangerouslySetInnerHTML={{ __html: orderTwoTrackerData?.achievedStatusMessage }} />
              </Badge>
            )}
          </div>
          <div className="flex flex-Column flex-align-center">
            <div data-testid={`icon-check-wrapper-${orderTwoTrackerData?.benefitName}`} className={styles.iconCheckCircularBackgroundBase}>
              <IconCheck
                size="xl"
                color="white"
                titleId={`icon-check-${orderTwoTrackerData?.benefitName}`}
                data-testid={`icon-check-${orderTwoTrackerData?.benefitName}`}
                isFilled={true}
                className={styles.iconCheckCircularBackgroundTop}
              />
            </div>
          </div>
        </div>
      )}
      {orderTwoTrackerData?.updateInterval && (
        <>
          <hr />
          <div className="margin-2-t">
            <span className="body-small">{orderTwoTrackerData.updateInterval}</span>
          </div>
        </>
      )}
    </Layout>
  );

  return (
    <>
      {renderTrackers()}
    </>
  );
};

export default UnifiedTracker;
